import styled from "@emotion/styled";
import { useEffect, useRef } from "react";
import MemAgentSlackConfirmationImage from "@/assets/images/mem-agent/mem-agent-slack-confirmation.png";
import MemAgentSlackConfirmationDarkImage from "@/assets/images/mem-agent/mem-agent-slack-confirmation-dark.png";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import type { MemAgentSlackConnectedModalViewEventParams } from "@/domains/metrics/tracked-event";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSIcon,
  MDSIconKind,
  MDSModalDialog,
  MDSThemedImage,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";

export interface MemAgentSlackConnectedModalViewProps {
  isOpen?: boolean;
  trackingContext?: MemAgentSlackConnectedModalViewEventParams | null;
  onClick: () => void;
  onDismiss: () => void;
}

interface MemAgentSlackConnectedModalProps {
  isOpen: boolean;
  trackingContext?: MemAgentSlackConnectedModalViewEventParams | null;
  onClick: () => void;
  onClose: () => void;
}

export const MemAgentSlackConnectedModal = ({
  isOpen,
  trackingContext,
  onClick,
  onClose,
}: MemAgentSlackConnectedModalProps) => {
  return (
    <MemAgentSlackConnectedModalView
      isOpen={isOpen}
      trackingContext={trackingContext}
      onClick={onClick}
      onDismiss={onClose}
    />
  );
};

export const MemAgentSlackConnectedModalView = ({
  isOpen = true,
  trackingContext,
  onClick,
  onDismiss,
}: MemAgentSlackConnectedModalViewProps) => {
  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedViewRef.current = false;
      return;
    }

    if (!trackingContext || hasTrackedViewRef.current) {
      return;
    }

    hasTrackedViewRef.current = true;
    trackEvent(TrackedEvent.MemAgentSlackConnectedModalView, trackingContext);
  }, [isOpen, trackingContext]);

  return (
    <StyledModalDialog isOpen={isOpen} handleClose={onDismiss} fluidWidth>
      <StyledHeaderContent slot={MDSDialogSlotName.Header}>
        <MDSText as="h1" color={MDSTextColor.Contrast} variant={MDSTextVariant.HeaderL}>
          Say hello to your AI chief of staff
        </MDSText>
      </StyledHeaderContent>

      <StyledContent slot={MDSDialogSlotName.Content}>
        <MDSText as="p" color={MDSTextColor.Primary} variant={MDSTextVariant.ParagraphS}>
          Check your Slack for a message from Mem Agent.
        </MDSText>
        <StyledSlackConfirmationImageFrame>
          <StyledSlackConfirmationImage
            alt="Slack message from Mem Agent introducing what it can do"
            lightSrc={MemAgentSlackConfirmationImage}
            darkSrc={MemAgentSlackConfirmationDarkImage}
          />
        </StyledSlackConfirmationImageFrame>
      </StyledContent>

      <StyledFooter slot={MDSDialogSlotName.Footer}>
        <MDSButton onClick={onClick} variant={MDSButtonVariant.Contrast}>
          <MDSIcon kind={MDSIconKind.Slack} slot={MDSButtonSlotName.LeftIcon} />
          Message Mem Agent in Slack
          <MDSIcon kind={MDSIconKind.ArrowUpRightFromSquare} slot={MDSButtonSlotName.RightIcon} />
        </MDSButton>
      </StyledFooter>
    </StyledModalDialog>
  );
};

const modalContentWidth = "min(584px, calc(90vw - 56px))";

const StyledModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    gap: ${({ theme }) => theme.spacing["4"]};

    button[aria-label="Close"] {
      margin-right: 0;
    }
  }
`;

const StyledHeaderContent = styled(MDSDialogTextContent)`
  && {
    padding-block: 0;
  }

  flex: 1 1 auto;
  min-width: 0;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
  width: ${modalContentWidth};
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  width: ${modalContentWidth};
`;

const StyledSlackConfirmationImageFrame = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.pane};
`;

const StyledSlackConfirmationImage = styled(MDSThemedImage)`
  display: block;
  width: 100%;
  height: auto;
`;
